import { Link } from 'react-router-dom';
import { HomeIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

export default function StudentCurrentAccount() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Student Current Account"
        description="Banking built for students in Somalia. A MyBank student current account with low requirements, digital access and no-fuss everyday banking."
        keywords={['student account Somalia', 'student bank account', 'MyBank student current account', 'youth banking']}
      />
      {/* Hero Section */}
      <div 
        className="relative min-h-[300px] sm:h-[390px] bg-gradient-to-r from-[#001F5F] to-[#017EFF] flex items-center justify-center"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-transparent" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col items-center space-y-6 sm:space-y-12 py-8 sm:py-0">
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-white/90 flex-wrap">
              <Link to="/" className="hover:text-[#017EFF] transition-colors flex items-center">
                <HomeIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Home
              </Link>
              <span className="text-white/60 mx-1 sm:mx-2">/</span>
              <Link to="/#banking-solutions" className="hover:text-[#017EFF] transition-colors hidden sm:inline">Personal/Retail Banking</Link>
              <span className="text-white/60 mx-1 sm:mx-2 hidden sm:inline">/</span>
              <Link to="/personal-current-account" className="hover:text-[#017EFF] transition-colors">Current Accounts</Link>
              <span className="text-white/60 mx-1 sm:mx-2">/</span>
              <span className="text-[#017EFF]">Student Current Account</span>
            </nav>
            
            {/* Hero Content */}
            <div className="flex flex-col items-center max-w-4xl mx-auto">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight text-center mb-4 sm:mb-6 px-2">
                Student <span className="text-[#017EFF]">Current Account</span>
              </h1>
              <p className="text-sm sm:text-lg md:text-xl text-white/90 text-center leading-relaxed px-4 max-w-3xl">
                Do things your way - Tailored banking solutions for students
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 -mt-8 sm:-mt-16 mb-12 sm:mb-24">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-start">
            {/* Left Column - Content */}
            <div className="space-y-6 sm:space-y-8 lg:space-y-10">
              <div className="space-y-4 sm:space-y-6">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#001F5F]">
                  Student-Friendly Banking
                </h2>
                
                <div className="space-y-4 text-gray-700">
                  <p className="text-sm sm:text-base leading-relaxed">
                    This type of account is designed for students of any level (undergraduate and postgraduate). The charges are at the lowest, and as a student, you just need the minimum balance to run your account with no annual fees collected from your account.
                  </p>
                  <p className="text-sm sm:text-base leading-relaxed">
                    With this account, students can shop globally, buy books/researches/audio-visual materials, subscribe to scientific and specialized magazines, buy courses, attend workshops, and pay education fees.
                  </p>
                  <p className="text-sm sm:text-base leading-relaxed">
                    Students with this type of account type can access our micro and students' financing products.
                  </p>
                </div>
              </div>

              {/* Benefits List */}
              <div className="bg-gray-50 rounded-lg sm:rounded-xl p-4 sm:p-6 lg:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#001F5F] mb-4 sm:mb-6 lg:mb-8">
                  Student Benefits
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:gap-6">
                  {[
                     "A widely accepted means of payment where you can pay Groceries, shops, Restaurants, Government agencies, transport companies, hotels, Universities, TV companies, Schools, etc.",
                     "50 leafs checkbook.",
                     "MasterCard Debit Card (Student rates to apply).",
                     "Ability to shop online with MasterCard and PayPal",
                     "No yearly ledger fees required",
                     "Automated digital bank statement sent on a monthly basis.",
                     "Convenient and reliable incoming & outgoing payments/remittances through Swift and other remittance channels and payment gateways.",
                     "Convenient bill payments (Water, Electricity, TVs, Schools, Universities, Visa, Business license, taxes, and other government duties, etc.)",
                     "Free access to our Economic updates, Business advice, workshops, and forums",
                     "Internet and Mobile Banking",
                     "SMS notifications of all transactions hitting your account",
                     "With us, your fingerprint is your ID. This is true for the services delivered through both branches and digital channels",
                     "Eligibility for students’ financing products",
                     "No minimum account opening balance is required",
                     "No minimum account operating balance is required",
                     "Full access to our agents across the nation."
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-center text-gray-700">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0">
                        <svg className="w-5 h-5 text-[#017EFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Button */}
              <div className="pt-4">
                <Link
                  to="/open-account"
                  className="inline-flex items-center px-8 py-4 bg-[#017EFF] text-white font-semibold rounded-lg hover:bg-[#0165CC] transition-all transform hover:scale-105 hover:shadow-lg"
                >
                  Open Student Account
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Right Column - Requirements and Additional Info */}
            <div className="space-y-8">
              {/* Requirements Section */}
              <div className="bg-[#001F5F] rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-white mb-8">
                  Requirements
                </h3>
                <div className="space-y-6">
                  {[
                    "Valid, certified copy of National ID, passport or any other ID document approved by the Central Bank of Somalia",
                    "The physical address of the customer",
                    "Proper declaration and documentation of the customer's source/volumes of income, nature of business and purpose of account opening",
                    "Two passport size recent photos of the individual",
                    "Valid student ID or admission letter from recognized institution"
                  ].map((requirement, index) => (
                    <div key={index} className="flex items-start text-white/90">
                      <div className="w-8 h-8 rounded-full bg-[#017EFF]/20 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                        <svg className="w-5 h-5 text-[#017EFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span>{requirement}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Links */}
              {/* <div className="bg-gray-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-[#001F5F] mb-6">
                  Student Services
                </h3>
                <div className="space-y-4">
                  <Link to="/internet-and-mobile-banking" className="flex items-center text-gray-700 hover:text-[#017EFF] transition-colors">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Mobile Banking
                  </Link>
                  <Link to="/debit-card" className="flex items-center text-gray-700 hover:text-[#017EFF] transition-colors">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Student Debit Card
                  </Link>
                  <Link to="/bill-payments" className="flex items-center text-gray-700 hover:text-[#017EFF] transition-colors">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Education Payments
                  </Link>
                  <Link to="/microfinance" className="flex items-center text-gray-700 hover:text-[#017EFF] transition-colors">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Student Financing
                  </Link>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 